import React from "react";
import "../css/EditCategory.css";
import ImageUploader from "@components/upload/ImageUploader";
import RichTextEditor from "@components/common/RichTextEditor";
import { useCategoriesQuery } from "@queries/catalog";

// html_details is rich HTML edited in a WYSIWYG editor. The mobile app renders it
// via html_entity_decode(htmlspecialchars_decode(...)), so storage must be
// htmlspecialchars-encoded HTML (e.g. "&lt;p&gt;...&lt;/p&gt;") — the same legacy
// format the old OpenCart Summernote produced.
const decodeEntities = (s) => {
  const ta = document.createElement("textarea");
  ta.innerHTML = String(s ?? "");
  return ta.value; // one level: &lt;->'<', &amp;->'&', &quot;->'"'
};
const looksLikeHtml = (s) =>
  /<\/?(p|div|br|ul|ol|li|span|strong|em|b|i|u|a|h[1-6])\b/i.test(String(s ?? ""));
// Stored value -> HTML the editor can ingest. AR rows are entity-encoded HTML;
// EN rows are often plain text with newlines (saved via the old plain textarea).
const htmlDetailsToEditor = (s) => {
  const raw = String(s ?? "");
  if (!raw) return "";
  const decoded = decodeEntities(raw);
  if (looksLikeHtml(decoded)) return decoded;
  return decoded.replace(/\r\n?|\n/g, "<br>"); // preserve plain-text line breaks
};
// Editor HTML -> stored value. Mirrors PHP htmlspecialchars(ENT_QUOTES).
const htmlDetailsFromEditor = (html) => {
  let s = String(html ?? "");
  if (s === "<p><br></p>" || s === "<p></p>") s = ""; // Quill's "empty" output
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Descriptions are stored/rendered as HTML on the app (like the old Summernote),
// but edited in a plain <textarea>. Turn stored HTML into clean editable text on
// load (decode entities incl. double-encoded &amp;nbsp;, line-break tags ->
// newlines, strip other tags); convert newlines back to <br> on save.
const htmlBrToText = (s) => {
  let str = String(s ?? "");
  if (!str) return "";
  str = str.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div)>/gi, "\n");
  const el = document.createElement("div");
  el.innerHTML = str;
  let text = el.textContent || el.innerText || "";
  text = text.replace(/&nbsp;/gi, " ").replace(/ /g, " ").replace(/&amp;/gi, "&");
  return text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");
};
const textToHtmlBr = (s) =>
  String(s ?? "").replace(/\r\n?|\n/g, "<br>");

function Tabs({ value, onChange, items, size = "md" }) {
  return (
    <div className={`oc-tabs ${size === "sm" ? "small" : ""}`}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          className={`oc-tab ${value === it.value ? "active" : ""}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export default function CategoryForm({ initial = {}, onSubmit, onCancel }) {
  const [tab, setTab] = React.useState("general");
  const [lang, setLang] = React.useState("ar");

  const [form, setForm] = React.useState(() => {
    const ar = initial.descriptions?.ar || {};
    const en = initial.descriptions?.en || {};

    return {
      id: initial.id ?? null,
      parent_id: initial.parent_id ?? 0,
      image: initial.image ?? "",
      top: Number(initial.top ?? 0),
      column: Number(initial.column ?? 1),
      sort_order: Number(initial.sort_order ?? 0),
      status: Number(initial.status ?? 1),
      checkout_flow: initial.checkout_flow ?? "",
      min_delivery_hours: initial.min_delivery_hours ?? "",
      descriptions: {
        ar: {
          language_id: ar.language_id ?? 2,
          name: ar.name ?? "",
          description: htmlBrToText(ar.description ?? ""),
          html_details: htmlDetailsToEditor(ar.html_details ?? ""),
          meta_title: ar.meta_title ?? "",
          meta_description: ar.meta_description ?? "",
          meta_keyword: ar.meta_keyword ?? ""
        },
        en: {
          language_id: en.language_id ?? 1,
          name: en.name ?? "",
          description: htmlBrToText(en.description ?? ""),
          html_details: htmlDetailsToEditor(en.html_details ?? ""),
          meta_title: en.meta_title ?? "",
          meta_description: en.meta_description ?? "",
          meta_keyword: en.meta_keyword ?? ""
        }
      }
    };
  });

  const [parentSearch, setParentSearch] = React.useState("");
  const [parentName, setParentName] = React.useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDesc = (loc, k, v) =>
    setForm((f) => ({
      ...f,
      descriptions: { ...f.descriptions, [loc]: { ...f.descriptions[loc], [k]: v } }
    }));

  const submit = (e) => {
    e?.preventDefault?.();
    // Persist descriptions as HTML so line breaks render on the app.
    const payload = {
      ...form,
      checkout_flow: form.checkout_flow || null,
      min_delivery_hours: form.min_delivery_hours === "" ? null : Number(form.min_delivery_hours),
      descriptions: {
        ar: {
          ...form.descriptions.ar,
          description: textToHtmlBr(form.descriptions.ar.description),
          html_details: htmlDetailsFromEditor(form.descriptions.ar.html_details),
        },
        en: {
          ...form.descriptions.en,
          description: textToHtmlBr(form.descriptions.en.description),
          html_details: htmlDetailsFromEditor(form.descriptions.en.html_details),
        },
      },
    };
    onSubmit?.(payload);
  };

  const D = form.descriptions[lang];

  const {
    data: categoriesData,
    isLoading: catsLoading,
    isError: catsError
  } = useCategoriesQuery(
    parentSearch ? { name: parentSearch } : {}, // adjust filter key if needed
    1,
    50
  );

  const parentOptions = categoriesData?.data || [];

  return (
    <form onSubmit={submit}>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "general", label: "General" },
          { value: "data", label: "Data" },
          { value: "seo", label: "SEO" },
          { value: "design", label: "Design" }
        ]}
      />

      {tab === "general" && (
        <div className="oc-tab-pane">
          <Tabs
            value={lang}
            onChange={setLang}
            items={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" }
            ]}
            size="sm"
          />

          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Category Name (Arabic)"
                  : "Category Name (English)"}
              </label>
              <input
                className="oc-input"
                value={D.name}
                onChange={(e) => setDesc(lang, "name", e.target.value)}
              />
            </div>

            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Html details (Arabic)"
                  : "Html details (English)"}
              </label>
              <RichTextEditor
                value={D.html_details}
                onChange={(html) => setDesc(lang, "html_details", html)}
                dir={lang === "ar" ? "rtl" : "ltr"}
              />
            </div>

            <div className="oc-field" style={{ gridColumn: "1 / span 2" }}>
              <label>
                {lang === "ar"
                  ? "Description (Arabic)"
                  : "Description (English)"}
              </label>
              <textarea
                className="oc-input"
                rows={8}
                value={D.description}
                onChange={(e) => setDesc(lang, "description", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className="oc-tab-pane">
          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            
          {/* Parent category selector */}
        <div className="oc-field">
          <label>Parent Category</label>
          <div className="oc-parent-select">
            <div className="oc-parent-row">
              <input
                className="oc-input"
                placeholder="Search categories..."
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
              />
              <button
                type="button"
                className="oc-btn ghost oc-parent-clear"
                onClick={() => {
                  setField("parent_id", 0);
                  setParentName("");
                }}
              >
                No parent
              </button>
            </div>

            <div className="oc-parent-current">
              {form.parent_id
                ? (
                  <>
                    <span className="oc-parent-current-label">Selected:</span>
                    <span className="oc-parent-current-value">
                      {parentName || "ID"}#{form.parent_id}
                    </span>
                  </>
                )
                : (
                  <>
                    <span className="oc-parent-current-label">Currently:</span>
                    <span className="oc-parent-current-value">root category</span>
                  </>
                )}
            </div>

            <div className="oc-parent-list">
              {catsLoading && (
                <div className="oc-parent-empty">Loading...</div>
              )}
              {catsError && (
                <div className="oc-parent-empty oc-field-error">
                  Error loading categories
                </div>
              )}
              {!catsLoading &&
                !catsError &&
                parentOptions.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={
                      "oc-parent-item" +
                      (form.parent_id === cat.id ? " active" : "")
                    }
                    onClick={() => {
                      setField("parent_id", Number(cat.id));
                      setParentName(cat.name || "");
                    }}
                  >
                    <span className="oc-parent-name">{cat.name}</span>
                    <span className="oc-parent-id">#{cat.id}</span>
                  </button>
                ))}
              {!catsLoading &&
                !catsError &&
                parentOptions.length === 0 &&
                parentSearch && (
                  <div className="oc-parent-empty">No categories found</div>
                )}
            </div>
          </div>
        </div>


            <div className="oc-field">
              <label>Image</label>
              <div className="oc-image-input-row">
                <input
                  className="oc-input"
                  value={form.image}
                  placeholder="img_xxx.png (path)"
                  onChange={(e) => setField("image", e.target.value)}
                />
                <ImageUploader
                  value={form.image}
                  onChange={(newPath) => {
                    if (newPath) setField("image", newPath);
                  }}
                />
              </div>
            </div>

            <div className="oc-field">
              <label>Top</label>
              <div style={{ display: "flex", alignItems: "center", height: 38 }}>
                <input
                  type="checkbox"
                  checked={!!form.top}
                  onChange={(e) => setField("top", e.target.checked ? 1 : 0)}
                />
              </div>
            </div>

            <div className="oc-field">
              <label>Columns</label>
              <input
                className="oc-input"
                type="number"
                value={form.column}
                onChange={(e) =>
                  setField("column", Number(e.target.value || 1))
                }
              />
            </div>

            <div className="oc-field">
              <label>Sort Order</label>
              <input
                className="oc-input"
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setField("sort_order", Number(e.target.value || 0))
                }
              />
            </div>

            <div className="oc-field">
              <label>Status</label>
              <select
                className="oc-select"
                value={form.status}
                onChange={(e) =>
                  setField("status", Number(e.target.value))
                }
              >
                <option value={1}>Enabled</option>
                <option value={0}>Disabled</option>
              </select>
            </div>

            <div className="oc-field">
              <label>Category type (checkout flow)</label>
              <select
                className="oc-select"
                value={form.checkout_flow}
                onChange={(e) => setField("checkout_flow", e.target.value)}
              >
                <option value="">Not set</option>
                <option value="outbound">Outbound (onsite cleaning service)</option>
                <option value="inbound">Inbound (Pickup, Clean &amp; Deliver)</option>
                <option value="delivery_only">Delivery only (no pickup - e.g. Shop)</option>
              </select>
              <small style={{ color: "#6b7280" }}>
                Drives which checkout flow the app shows for this category —
                a single onsite arrival window (Outbound) vs pickup date/time
                + drop-off date/time (Inbound). Set this whenever a new
                service category is added.
              </small>
            </div>

            <div className="oc-field">
              <label>Min. delivery window (hours)</label>
              <input
                className="oc-input"
                type="number"
                min="1"
                placeholder="e.g. 72"
                value={form.min_delivery_hours}
                onChange={(e) => setField("min_delivery_hours", e.target.value)}
              />
              <small style={{ color: "#6b7280" }}>
                Inbound categories only. Minimum pickup→drop-off turnaround
                for products in this category that don't have their own
                "Delivery within" set. Leave blank to use the site default
                (48h).
              </small>
            </div>
          </div>
        </div>
      )}

      {tab === "seo" && (
        <div className="oc-tab-pane">
          <Tabs
            value={lang}
            onChange={setLang}
            items={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" }
            ]}
            size="sm"
          />
          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Meta Title (Arabic)"
                  : "Meta Title (English)"}
              </label>
              <input
                className="oc-input"
                value={D.meta_title}
                onChange={(e) => setDesc(lang, "meta_title", e.target.value)}
              />
            </div>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Meta Keywords (Arabic)"
                  : "Meta Keywords (English)"}
              </label>
              <input
                className="oc-input"
                value={D.meta_keyword}
                onChange={(e) => setDesc(lang, "meta_keyword", e.target.value)}
              />
            </div>
            <div className="oc-field" style={{ gridColumn: "1 / span 2" }}>
              <label>
                {lang === "ar"
                  ? "Meta Description (Arabic)"
                  : "Meta Description (English)"}
              </label>
              <textarea
                className="oc-input"
                rows={4}
                value={D.meta_description}
                onChange={(e) =>
                  setDesc(lang, "meta_description", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      )}

      {tab === "design" && (
        <div className="oc-tab-pane">
          <div className="oc-muted">No fields</div>
        </div>
      )}

      <div
        className="oc-actions"
        style={{ marginTop: 16, display: "flex", gap: 8 }}
      >
        <button className="oc-btn" type="submit">
          Save
        </button>
        <button
          className="oc-btn ghost"
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

